import { Link } from 'react-router-dom';

const failures = [
  { text: 'Systems go down without warning', icon: 'ri-server-line' },
  { text: 'Employees can\'t work', icon: 'ri-user-unfollow-line' },
  { text: 'Customers can\'t reach you', icon: 'ri-phone-off-line' },
  { text: 'Revenue is lost by the hour', icon: 'ri-money-dollar-circle-line' },
  { text: 'Data may be unrecoverable', icon: 'ri-database-2-line' },
];

const PainPoints = () => {
  return (
    <section className="relative py-24 lg:py-36 overflow-hidden">

      {/* ── Background Image ── */}
      <div className="absolute inset-0 w-full h-full">
        <img
          src="https://public.readdy.ai/ai/img_res/e85c732f74366af145af483f65fbdec4.jpg"
          alt="Business risk visualization"
          className="w-full h-full object-cover object-center"
        />
      </div>

      {/* ── Overlays ── */}
      <div className="absolute inset-0 bg-slate-950/78" />
      <div className="absolute inset-0 bg-gradient-to-r from-slate-950/95 via-slate-950/70 to-slate-950/50" />
      <div className="absolute inset-0 bg-gradient-to-t from-slate-950 via-transparent to-slate-950/60" />
      {/* Red atmospheric tint — conveys danger */}
      <div className="absolute top-0 left-0 w-[500px] h-[500px] rounded-full bg-red-900/10 blur-[100px] pointer-events-none" />
      <div className="absolute bottom-0 right-0 w-[400px] h-[400px] rounded-full bg-teal-900/12 blur-[80px] pointer-events-none" />

      {/* ── Content ── */}
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-16 xl:gap-24 items-center">

          {/* Left: Problem */}
          <div>
            <div className="inline-flex items-center gap-2 border border-red-500/30 bg-red-500/10 backdrop-blur-sm rounded-full px-3.5 py-1.5 mb-7">
              <span className="w-1.5 h-1.5 rounded-full bg-red-400" />
              <span className="text-red-400 text-xs font-bold tracking-widest uppercase">The Real Risk</span>
            </div>

            <h2 className="text-4xl lg:text-5xl font-extrabold text-white leading-[1.05] tracking-tight mb-5">
              When IT Fails,<br />
              <span className="text-slate-400">Your Business Stops</span>
            </h2>

            <p className="text-slate-400 text-base leading-relaxed mb-10 max-w-sm">
              Every minute of unplanned downtime has a real cost. These aren't edge cases — they happen to businesses without the right protection.
            </p>

            {/* Failure list — glass style */}
            <ul className="space-y-0 mb-10">
              {failures.map((item, i) => (
                <li
                  key={i}
                  className="flex items-center gap-4 py-4 border-b border-white/8 last:border-0"
                >
                  <div
                    className="w-9 h-9 flex-shrink-0 flex items-center justify-center rounded-lg border border-red-500/20"
                    style={{ background: 'rgba(239,68,68,0.08)', backdropFilter: 'blur(8px)' }}
                  >
                    <i className={`${item.icon} text-sm text-red-400`} />
                  </div>
                  <p className="text-slate-200 text-base font-medium">{item.text}</p>
                </li>
              ))}
            </ul>

            <p className="text-2xl font-extrabold text-teal-400 tracking-tight">We prevent that.</p>
          </div>

          {/* Right: Solution card — glass morphism */}
          <div>
            <div
              className="relative rounded-3xl p-8 lg:p-10 overflow-hidden border border-white/10"
              style={{ background: 'rgba(2, 8, 20, 0.72)', backdropFilter: 'blur(24px)' }}
            >
              {/* Subtle glow */}
              <div className="absolute top-0 right-0 w-64 h-64 bg-teal-500/8 rounded-full blur-3xl pointer-events-none" />

              <div className="relative">
                {/* Header */}
                <div className="flex items-center gap-3 mb-8 pb-6 border-b border-white/8">
                  <div className="w-10 h-10 flex items-center justify-center bg-teal-500/15 border border-teal-500/25 rounded-xl">
                    <i className="ri-shield-check-fill text-teal-400 text-lg" />
                  </div>
                  <div>
                    <p className="text-white font-bold text-sm leading-none mb-0.5">Mirrored Storage Protection</p>
                    <p className="text-teal-400 text-xs font-medium">Business Continuity Partner</p>
                  </div>
                  <div className="ml-auto flex items-center gap-1.5 bg-emerald-500/10 border border-emerald-500/20 rounded-full px-3 py-1">
                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
                    <span className="text-emerald-400 text-xs font-bold">Active</span>
                  </div>
                </div>

                {/* 4 protection cards — glass inner */}
                <div className="grid sm:grid-cols-2 gap-3 mb-8">
                  {[
                    { icon: 'ri-radar-line', title: '24/7 Live Monitoring', desc: 'Threats caught and resolved before they become outages.' },
                    { icon: 'ri-database-2-line', title: 'Tested Backups', desc: 'Not assumed — proven, verified, and ready to restore.' },
                    { icon: 'ri-timer-flash-line', title: 'Rapid Recovery', desc: 'Systems restored in minutes, not days.' },
                    { icon: 'ri-map-pin-2-line', title: 'Local DFW Team', desc: 'Real people who pick up the phone immediately.' },
                  ].map((pt, i) => (
                    <div
                      key={i}
                      className="rounded-xl p-5 border border-white/8 hover:border-teal-500/30 transition-colors duration-200"
                      style={{ background: 'rgba(255,255,255,0.04)' }}
                    >
                      <div className="w-8 h-8 flex items-center justify-center bg-teal-500/12 rounded-lg mb-3">
                        <i className={`${pt.icon} text-teal-400 text-sm`} />
                      </div>
                      <p className="text-white font-semibold text-sm mb-1.5">{pt.title}</p>
                      <p className="text-slate-400 text-xs leading-relaxed">{pt.desc}</p>
                    </div>
                  ))}
                </div>

                <Link
                  to="/locations"
                  className="inline-flex items-center gap-2 bg-teal-500 hover:bg-teal-400 text-white font-bold px-6 py-3 rounded-lg transition-all whitespace-nowrap cursor-pointer text-sm"
                >
                  Get a Free Assessment
                  <i className="ri-arrow-right-line" />
                </Link>
              </div>
            </div>
          </div>

        </div>
      </div>
    </section>
  );
};

export default PainPoints;
